import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Avatar } from 'antd-mobile';
import { request } from '@/lib/api';
import { useI18n } from '@/hooks/useI18n';
import ManyDotLoading from '@/components/Loaders/ManyDotLoading';
import PCMaxWidthContainer from '@/components/Layout/PCMaxWidthContainer';
import rankBg from '@/assets/images/ai/rankBg.png';
import zuan1 from '@/assets/images/ai/zuan1.png';
import zuan2 from '@/assets/images/ai/zuan2.png';
import zuan3 from '@/assets/images/ai/zuan3.png';
import nameBg from '@/assets/images/ai/nameBg.png';
import err1 from '@/assets/images/ai/err1.png';

// 排行榜数据类型
interface RankItem {
  user_id: string;
  dept_user_name: string;
  avatar: string;
  avatar_color?: string;
  max_score: number;
  max_score_used_time: number; // 练习时长（秒）
}

const RankList: React.FC = () => {
  const navigate = useNavigate();
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const { t } = useI18n();
  const [rankList, setRankList] = useState<any[]>([]);
  const [otherRankList, setOtherRankList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [finishLoading, setFinishLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 默认头像URL
  const defaultAvatarUrl = 'https://newsokeeditorcdn.soke.cn/public/ai/user_avatars/default.png';

  // 显示整分
  const showScore = (score: number | string) => {
    return score ? parseInt(score.toString()) : '-';
  };

  // 获取使用时间
  const getUsedTime = (seconds: number) => {
    const usedMinute = Math.floor(seconds / 60);
    const usedSecond = seconds % 60;
    return `${usedMinute}${t('分钟')}${usedSecond}${t('秒')}`;
  };

  // 获取排行榜数据
  const getRankList = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await request('/supervisor-assigns/scenario-leaderboard', {
        method: 'GET',
        params: {
          scenario_id: scenarioId
        }
      });

      let rankData = response.data || [];

      // 展示前十名的数据，不足10名用空数据填充
      if (rankData.length < 10) {
        const num = 10 - rankData.length;
        for (let i = 0; i < num; i++) {
          rankData.push({
            avatar: 'icon',
            dept_user_name: '',
            max_score_used_time: 0,
            max_score: 0,
            avatar_color: '#DCDFE6'
          });
        }
      }

      setRankList(rankData.slice(0, 3)); // 前三名
      setOtherRankList(rankData.slice(3)); // 其他排名

      if (rankData.length === 0) {
        setFinishLoading(true);
      }

      setLoading(false);
      setFinishLoading(true);
    } catch (err) {
      console.error('获取排行榜数据失败:', err);
      setError(t('获取排行榜数据失败，请稍后重试'));
      setLoading(false);
      setFinishLoading(true);
    }
  };

  useEffect(() => {
    getRankList();
  }, [scenarioId]);

  return (
    <div
      className="min-h-screen overflow-auto"
      style={{
        background: 'linear-gradient(180deg, #DDEDFF 0%, #E0E4FF 19.71%, #E9E6FF 39.42%)'
      }}
    >
      <PCMaxWidthContainer bgGradient='' maxWidth="800px">
      {/* 响应式容器 */}
      <div className="w-full px-0">
        {/* 加载状态 */}
        <AnimatePresence>
          {loading && (
            <motion.div
              className="fixed inset-0 flex justify-center items-center z-50"
              style={{ background: 'rgba(255, 255, 255, 0.6)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="flex justify-center items-center"
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.7, 1, 0.7]
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <div className="">
                  <ManyDotLoading />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 标题 */}
        <motion.div
          className="text-[#0A1B39] text-center font-semibold pt-6 text-2xl md:text-3xl lg:text-4xl"
          style={{ fontWeight: 600, marginBottom: '103px' }}
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {t('ai.rank.title', '排行榜')}
        </motion.div>

        {error ? (
          <motion.div
            className="flex flex-col items-center justify-center"
            style={{ minHeight: '300px', padding: '40px 20px' }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img src={err1} alt={t('错误')} style={{ width: '80px', height: '80px', marginBottom: '16px' }} />
            <p style={{ fontSize: '16px', textAlign: 'center', margin: 0, color: '#fff', marginBottom: '16px' }}>{error}</p>
            <button
              onClick={getRankList}
              style={{
                padding: '8px 16px',
                backgroundColor: '#007AFF',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {t('重试')}
            </button>
          </motion.div>
        ) : rankList && rankList.length > 0 ? (
          <>
            {/* Top 3 展示区 */}
            <motion.div
              className="flex justify-around mt-6 px-10 md:px-16 lg:px-20"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            >
              {/* 第二名 */}
              {rankList[1] && (
                <motion.div
                  className="flex flex-col items-center"
                  initial={{ opacity: 0, scale: 0.8, y: 30 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="relative">
                    <Avatar
                      src={rankList[1].avatar && rankList[1].avatar !== 'icon' ? rankList[1].avatar : defaultAvatarUrl}
                      fallback={rankList[1].dept_user_name ? rankList[1].dept_user_name.substr(rankList[1].dept_user_name.length - 2) : '👤'}
                      style={{
                        '--size': '80px',
                        '--border-radius': '50%',
                        border: '2px solid white',
                        backgroundColor: rankList[1].avatar_color || '#DCDFE6'
                      }}
                    />
                    <div className="absolute" style={{ right: '-4px', top: '-4px' }}>
                      <img src={zuan2} alt="" className="w-6 h-6" />
                      <div
                        className="absolute top-0 text-white text-center"
                        style={{
                          left: '7px',
                          fontSize: '14px',
                          lineHeight: '22px'
                        }}
                      >
                        2
                      </div>
                    </div>
                  </div>
                  <div
                    className="text-white text-center min-w-[120px] mt-2 text-sm p-2 text-nowrap overflow-hidden text-ellipsis"
                    style={{
                      background: 'linear-gradient(90deg, rgba(149, 139, 255, 0.00) 1%, #B8B2FF 54.5%, rgba(149, 139, 255, 0.00) 99.5%)',
                      maxWidth: '100px'
                    }}
                    title={rankList[1].dept_user_name || ''}
                  >
                    {rankList[1].dept_user_name || t('ai.rank.vacant', '虚位以待')}
                  </div>
                  {rankList[1].max_score !== 0 && (
                    <div className="text-white font-semibold mt-1 text-xl">
                      <span  className='text-[#000000] mr-1'>{showScore(rankList[1].max_score)}</span>
                      <span className="text-xs text-[#86909C]">{t('分')}</span>
                    </div>
                  )}
                </motion.div>
              )}

              {/* 第一名 */}
              {rankList[0] && (
                <motion.div
                  className="flex flex-col items-center mt-2"
                  initial={{ opacity: 0, scale: 0.8, y: 30 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  whileHover={{ scale: 1.08 }}
                >
                  <div className="relative" style={{ marginTop: '-60px' }}>
                    <Avatar
                      src={rankList[0].avatar && rankList[0].avatar !== 'icon' ? rankList[0].avatar : defaultAvatarUrl}
                      fallback={rankList[0].dept_user_name ? rankList[0].dept_user_name.substr(rankList[0].dept_user_name.length - 2) : '👤'}
                      style={{
                        '--size': '120px',
                        '--border-radius': '50%',
                        border: '2px solid white',
                        backgroundColor: rankList[0].avatar_color || '#DCDFE6',
                        fontSize: '18px'
                      }}
                    />
                    <div className="absolute" style={{ right: '-4px', top: '-4px' }}>
                      <img src={zuan1} alt="" className="w-6 h-6" />
                      <div
                        className="absolute top-0 text-white text-center"
                        style={{
                          left: '3.5px',
                          fontSize: '14px',
                          lineHeight: '22px'
                        }}
                      >
                        1
                      </div>
                    </div>
                  </div>
                  <div
                    className="text-white text-center min-w-[120px] mt-2 text-sm p-2 text-nowrap overflow-hidden text-ellipsis"
                    style={{
                      background: 'linear-gradient(90deg, rgba(149, 139, 255, 0.00) 1%, #B8B2FF 54.5%, rgba(149, 139, 255, 0.00) 99.5%)',
                      maxWidth: '120px'
                    }}
                    title={rankList[0].dept_user_name || ''}
                  >
                    {rankList[0].dept_user_name || t('ai.rank.vacant', '虚位以待')}
                  </div>
                  {rankList[0].max_score !== 0 && (
                    <div className="text-white font-semibold mt-1 text-xl">
                      <span className='text-[#000000] mr-1'>{showScore(rankList[0].max_score)}</span>
                      <span className="text-xs text-[#86909C]">{t('分')}</span>
                    </div>
                  )}
                </motion.div>
              )}

              {/* 第三名 */}
              {rankList[2] && (
                <motion.div
                  className="flex flex-col items-center"
                  initial={{ opacity: 0, scale: 0.8, y: 30 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="relative">
                    <Avatar
                      src={rankList[2].avatar && rankList[2].avatar !== 'icon' ? rankList[2].avatar : defaultAvatarUrl}
                      fallback={rankList[2].dept_user_name ? rankList[2].dept_user_name.substr(rankList[2].dept_user_name.length - 2) : '👤'}
                      style={{
                        '--size': '80px',
                        '--border-radius': '50%',
                        border: '2px solid white',
                        backgroundColor: rankList[2].avatar_color || '#DCDFE6'
                      }}
                    />
                    <div className="absolute" style={{ right: '-4px', top: '-4px' }}>
                      <img src={zuan3} alt="" className="w-6 h-6" />
                      <div
                        className="absolute top-0 text-white text-center"
                        style={{
                          left: '7px',
                          fontSize: '14px',
                          lineHeight: '22px'
                        }}
                      >
                        3
                      </div>
                    </div>
                  </div>
                  <div
                    className="text-white text-center min-w-[120px] mt-2 text-sm p-2 text-nowrap overflow-hidden text-ellipsis"
                    style={{
                      background: 'linear-gradient(90deg, rgba(149, 139, 255, 0.00) 1%, #B8B2FF 54.5%, rgba(149, 139, 255, 0.00) 99.5%)',
                      maxWidth: '100px'
                    }}
                    title={rankList[2].dept_user_name || ''}
                  >
                    {rankList[2].dept_user_name || t('ai.rank.vacant', '虚位以待')}
                  </div>
                  {rankList[2].max_score !== 0 && (
                    <div className="text-white font-semibold mt-1 text-xl">
                      <span className='text-[#000000] mr-1'>{showScore(rankList[2].max_score)}</span>
                      <span className="text-xs text-[#86909C]">{t('分')}</span>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>

            {/* 排行榜列表 */}
            {otherRankList && otherRankList.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.0 }}
                className="w-full bg-white mt-12 px-4 md:px-8 lg:px-12"
                style={{
                  borderRadius: '48px 48px 0 0',
                  paddingTop: '16px'
                }}
              >
                {otherRankList.map((item, index) => (
                  <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 1.2 + index * 0.1 }}
                    whileHover={{ scale: 1.02, backgroundColor: '#f8f9fa' }}
                    key={index}
                    className="flex items-center justify-between border-b-2 last:border-b-0"
                    style={{
                      paddingTop: '24px',
                      paddingBottom: '24px',
                      borderBottomColor: '#ebeef5'
                    }}
                  >
                    <div className="flex items-center">
                      <div
                        className="text-center font-semibold text-center"
                        style={{
                          fontSize: '22px',
                          color: '#909399',
                          fontWeight: 600,
                          lineHeight: '22px',
                          width: '40px',
                          minWidth: '40px'
                        }}
                      >
                        {index + 4}
                      </div>
                      <Avatar
                        src={item.avatar && item.avatar !== 'icon' ? item.avatar : defaultAvatarUrl}
                        fallback={item.dept_user_name ? item.dept_user_name.substr(item.dept_user_name.length - 2) : '👤'}
                        style={{
                          '--size': '40px',
                          '--border-radius': '50%',
                          marginLeft: '12px',
                          marginRight: '8px',
                          backgroundColor: item.avatar_color || '#DCDFE6'
                        }}
                      />
                      <div className="text-left">
                        <div
                          className="text-sm md:text-base lg:text-lg text-nowrap overflow-hidden text-ellipsis"
                          style={{
                            color: '#606266',
                            fontWeight: 500,
                            lineHeight: '22px',
                            maxWidth: '120px'
                          }}
                          title={item.dept_user_name || ''}
                        >
                          {item.dept_user_name || t('ai.rank.vacant', '虚位以待')}
                        </div>
                        {item.avatar !== 'icon' && (
                          <div
                            className="mt-1 text-xs md:text-sm"
                            style={{
                              color: '#909399',
                              fontWeight: 400,
                              lineHeight: '18px'
                            }}
                          >
                            {t('ai.rank.practice', '练习')}{getUsedTime(item.max_score_used_time)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div
                      className="font-semibold text-xl md:text-2xl lg:text-3xl"
                      style={{
                        color: '#303133',
                        fontWeight: 600,
                        lineHeight: '22px'
                      }}
                    >
                      {item.max_score !== 0 && (
                        <>
                          <span>{showScore(item.max_score)}</span>
                          <span className="text-xs md:text-sm" style={{ color: '#909399', lineHeight: '18px' }}>{t('分')}</span>
                        </>
                      )}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </>
        ) : finishLoading ? (
          <motion.div
            className="flex flex-col items-center"
            style={{ minHeight: '200px' }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img src={err1} alt="" style={{ width: '72px' }} />
            <div
              className="text-center mt-4"
              style={{
                fontSize: '14px',
                color: '#909399',
                fontWeight: 400
              }}
            >
              {t('common.noData', '暂无数据')}
            </div>
          </motion.div>
        ) : null}
      </div>
      </PCMaxWidthContainer>
    </div>
  );
};

export default RankList;
